<script setup>
import ReportFilterSelector from './FilterSelector.vue';
import SummaryReportLink from './SummaryReportLink.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedTeamId = ref(null);

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];
const selectedTeamMembers = computed(() => {
  if (!selectedTeamId.value) {
    return [];
  }

  return store.getters['teamMembers/getTeamMembers'](selectedTeamId.value);
});
const filteredRowItems = computed(() => {
  if (props.type !== 'agent' || !selectedTeamId.value) {
    return rowItems.value;
  }

  const memberIds = new Set(
    selectedTeamMembers.value.map(member => Number(member.id))
  );
  return rowItems.value.filter(row => memberIds.has(Number(row.id)));
});

const getMetrics = (id) =>
  reportMetrics.value.find((metrics) => metrics.id === Number(id)) || {};
const columnHelper = createColumnHelper();
const { t } = useI18n();

const defaulSpanRender = (cellProps) =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

const renderConversationBreakdown = (v) => {
  if (v == null) return '--';
  const unresolved = v.unresolved ?? v.open ?? 0;
  const resolved = v.resolved ?? 0;
  const total = v.total ?? unresolved + resolved;

  return `${total.toLocaleString()} / ${unresolved.toLocaleString()} / ${resolved.toLocaleString()}`;
};

const conversationCell = (cellProps) => {
  const text = renderConversationBreakdown(cellProps.getValue());
  return h('span', { class: text === '--' ? 'text-n-slate-12' : '' }, text);
};

const columns = [
  columnHelper.accessor('name', {
    header: t(`SUMMARY_REPORTS.${props.type.toUpperCase()}`),
    width: 300,
    cell: (cellProps) => h(SummaryReportLink, cellProps),
  }),
  columnHelper.accessor('messageCount', {
    header: t('SUMMARY_REPORTS.MESSAGE_COUNT'),
    width: 140,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('responseBucket0', {
    header: t('SUMMARY_REPORTS.RESPONSE_0_15'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('responseBucket1', {
    header: t('SUMMARY_REPORTS.RESPONSE_15_30'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('responseBucket2', {
    header: t('SUMMARY_REPORTS.RESPONSE_30_60'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('responseBucket3', {
    header: t('SUMMARY_REPORTS.RESPONSE_60_PLUS'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('prevDayResponsesCount', {
    header: t('SUMMARY_REPORTS.PREV_DAY_RESPONSES'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('handledConversationsCount', {
    header: t('SUMMARY_REPORTS.HANDLED_CONVERSATIONS'),
    width: 170,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('repliedConversationsCount', {
    header: t('SUMMARY_REPORTS.REPLIED_CONVERSATIONS'),
    width: 170,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('resolvedConversationsDistinctCount', {
    header: t('SUMMARY_REPORTS.RESOLVED_CONVERSATIONS_DISTINCT'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('resolutionEventsCount', {
    header: t('SUMMARY_REPORTS.RESOLUTION_EVENTS'),
    width: 160,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('assignedOpenNowCount', {
    header: t('SUMMARY_REPORTS.ASSIGNED_OPEN_NOW'),
    width: 160,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('assignedOpenStaleCount', {
    header: t('SUMMARY_REPORTS.ASSIGNED_OPEN_STALE'),
    width: 220,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgFirstResponseTime', {
    header: t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgResolutionTime', {
    header: t('SUMMARY_REPORTS.AVG_RESOLUTION_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgReplyTime', {
    header: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('assigneeAssignsPerformed', {
    header: t('SUMMARY_REPORTS.ASSIGNEE_ASSIGNS_PERFORMED'),
    width: 170,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('assigneeAssignsReceived', {
    header: t('SUMMARY_REPORTS.ASSIGNEE_ASSIGNS_RECEIVED'),
    width: 140,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('teamAssignsPerformed', {
    header: t('SUMMARY_REPORTS.TEAM_ASSIGNS_PERFORMED'),
    width: 170,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('csatCount', {
    header: t('SUMMARY_REPORTS.CSAT_COUNT'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('csatAvg', {
    header: t('SUMMARY_REPORTS.CSAT_AVG'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('csatBadCount', {
    header: t('SUMMARY_REPORTS.CSAT_BAD_COUNT'),
    width: 160,
    cell: defaulSpanRender,
  }),
];

const renderAvgTime = (value) => (value ? formatTime(value) : '--');

const renderCount = (value) => (value ? value.toLocaleString() : '--');

const renderFloat = (v) => (v || v === 0 ? Number(v).toFixed(2) : '--');

const tableData = computed(() =>
  filteredRowItems.value.map((row) => {
    const rowMetrics = getMetrics(row.id);
    const {
      messageCount,
      prevDayResponsesCount,
      csatCount,
      csatAvg,
      csatBadCount,
      assigneeAssignsPerformed,
      assigneeAssignsReceived,
      teamAssignsPerformed,

      repliedConversationsCount,
      handledConversationsCount,
      handledNotResolvedConversationsCount,
      resolvedConversationsDistinctCount,
      resolutionEventsCount,
      assignedOpenNowCount,
      assignedOpenStaleCount,

      conversationsCount,
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      agentResponseTimeBuckets,
    } = rowMetrics;
    return {
      id: row.id,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      type: props.type,
      messageCount: renderCount(messageCount),
      prevDayResponsesCount: renderCount(prevDayResponsesCount),
      csatCount: renderCount(csatCount),
      csatAvg: renderFloat(csatAvg),
      csatBadCount: renderCount(csatBadCount),
      assigneeAssignsPerformed: renderCount(assigneeAssignsPerformed),
      assigneeAssignsReceived: renderCount(assigneeAssignsReceived),
      teamAssignsPerformed: renderCount(teamAssignsPerformed),

      repliedConversationsCount: renderCount(repliedConversationsCount),
      handledConversationsCount: renderCount(handledConversationsCount),
      handledNotResolvedConversationsCount: renderCount(
        handledNotResolvedConversationsCount
      ),
      resolvedConversationsDistinctCount: renderCount(
        resolvedConversationsDistinctCount
      ),
      resolutionEventsCount: renderCount(resolutionEventsCount),
      assignedOpenNowCount: renderCount(assignedOpenNowCount),
      assignedOpenStaleCount: renderCount(assignedOpenStaleCount),
      conversationsCount,

      avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
      avgReplyTime: renderAvgTime(avgReplyTime),
      avgResolutionTime: renderAvgTime(avgResolutionTime),
      responseBucket0: agentResponseTimeBuckets
        ? renderCount(agentResponseTimeBuckets['0'])
        : '--',
      responseBucket1: agentResponseTimeBuckets
        ? renderCount(agentResponseTimeBuckets['1'])
        : '--',
      responseBucket2: agentResponseTimeBuckets
        ? renderCount(agentResponseTimeBuckets['2'])
        : '--',
      responseBucket3: agentResponseTimeBuckets
        ? renderCount(agentResponseTimeBuckets['3'])
        : '--',
    };
  })
);

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  store.dispatch(props.actionKey, {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  });
};

onMounted(() => fetchAllData());

const onFilterChange = (updatedFilter) => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  const teamId = updatedFilter.selectedTeam?.id || null;

  if (props.type === 'agent' && teamId && teamId !== selectedTeamId.value) {
    store.dispatch('teamMembers/get', { teamId });
  }

  selectedTeamId.value = teamId;
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = (options = {}) => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const { fileType, ...downloadOptions } = options;
    const fileName = generateFileName({
      type: fileType || props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
      ...downloadOptions,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFilterSelector
    use-from-until-range-selector
    :show-team-filter="type === 'agent'"
    :from-until-helper-text="
      type === 'agent' ? t('SUMMARY_REPORTS.RANGE_LIMITATION_NOTE') : ''
    "
    @filter-change="onFilterChange"
  />
  <div class="flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2">
    <Table :table="table" />
  </div>
</template>
